import { Hourglass, RefreshCw } from 'lucide-react';
import type { Device, User } from '../../types/user';
import { shortFingerprint } from '../../core/device';
import { ROLE_LABELS } from '../../core/permissions';
import { AppSignature, AuthShell } from './AuthShell';

interface PendingApprovalViewProps {
  user: User;
  device: Device;
  onRecheck: () => void;
  onCancel: () => void;
  busy?: boolean;
}

/** Shown when the device fingerprint still awaits a super-admin approval. */
export function PendingApprovalView({
  user,
  device,
  onRecheck,
  onCancel,
  busy = false,
}: PendingApprovalViewProps) {
  return (
    <AuthShell
      icon={<Hourglass size={22} aria-hidden />}
      title="המכשיר ממתין לאישור"
      subtitle="מנהל על צריך לאשר את המכשיר הזה לפני הכניסה הראשונה"
      footer={<AppSignature />}
    >
      <dl className="rounded-xl border border-line divide-y divide-line text-sm">
        <div className="flex items-center justify-between gap-3 p-3">
          <dt className="text-ink/60">משתמש</dt>
          <dd className="font-medium">
            {user.fullName} · {ROLE_LABELS[user.role]}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3 p-3">
          <dt className="text-ink/60">מכשיר</dt>
          <dd className="font-medium">{device.deviceName}</dd>
        </div>
        <div className="flex items-center justify-between gap-3 p-3">
          <dt className="text-ink/60">טביעת אצבע</dt>
          <dd className="font-mono text-xs" data-dir="ltr">
            {shortFingerprint(device.deviceFingerprint)}
          </dd>
        </div>
      </dl>

      <p className="mt-4 text-sm text-ink/60 text-center">
        מסרו את מזהה המכשיר למנהל העל. לאחר האישור לחצו על בדיקה מחדש.
      </p>

      <button
        type="button"
        onClick={onRecheck}
        disabled={busy}
        className="mt-5 w-full h-12 rounded-xl bg-brand-500 text-white font-semibold transition-colors hover:bg-brand-700 disabled:opacity-40 flex items-center justify-center gap-2"
      >
        <RefreshCw size={18} aria-hidden className={busy ? 'animate-spin' : ''} />
        {busy ? 'בודק…' : 'בדיקה מחדש'}
      </button>

      <button
        type="button"
        onClick={onCancel}
        className="mt-4 w-full text-sm text-ink/60 hover:text-ink"
      >
        חזרה למסך הכניסה
      </button>
    </AuthShell>
  );
}
